import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-hot-toast';
import useUserService from 'shared/api/services/userService';
import { useAuth } from 'shared/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { getSessionProfile } from 'shared/utils/sessionProfileStore';
import { complexPasswordField } from 'shared/utils/passwordPolicy';
import PasswordRequirementsList from 'shared/components/PasswordRequirementsList';
import {
  buildTenantLoginPath,
  getTenantIdFromWindowLocation,
  resolveTenantPublicPathKey,
} from 'shared/utils/tenantRoute';

const schema = yup.object({
  currentPassword: yup.string().required('Current (temporary) password is required'),
  password: complexPasswordField(yup).notOneOf(
    [yup.ref('currentPassword')],
    'New password must be different from the current password'
  ),
  confirmedPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Confirm your new password'),
});

const FirstLoginChangePassword = () => {
  const { changePassword } = useUserService();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);
  const userId = getSessionProfile()?.id;
  const profile = getSessionProfile();
  const rotationPolicyEnabled = Boolean(profile?.tenantSettings?.forcePasswordRotationEnabled);
  const rotationMonths = profile?.tenantSettings?.passwordRotationMonths ?? 4;

  const introText = rotationPolicyEnabled
    ? `Your organization requires passwords to be changed every ${rotationMonths} months. Enter your current password and choose a new one before continuing.`
    : 'You signed in with a temporary password. Choose a strong new password before continuing. You will use this new password on your next login.';

  const currentPasswordLabel = rotationPolicyEnabled
    ? 'Current password'
    : 'Current password (temporary)';

  const passwordVisibilityAdornment = (visible, onToggle) => (
    <InputAdornment position="end">
      <IconButton
        aria-label={visible ? 'Hide password' : 'Show password'}
        onClick={onToggle}
        onMouseDown={(e) => e.preventDefault()}
        edge="end"
        size="small"
      >
        {visible ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const handleSubmit = async (values) => {
    if (!userId) {
      toast.error('Session expired. Please sign in again.');
      navigate('/notFound', { replace: true });
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(userId, {
        currentPassword: values.currentPassword,
        password: values.password,
        confirmedPassword: values.confirmedPassword,
      });
      // End the temporary-password session and return to tenant login.
      try {
        await signOut(profile?.tenantId);
      } catch {
        const loginPath = buildTenantLoginPath(
          resolveTenantPublicPathKey({
            name: profile?.tenantBranding?.name || profile?.tenantName,
            id: profile?.tenantId,
            fallback: getTenantIdFromWindowLocation() || 'login',
          })
        );
        navigate(loginPath, { replace: true });
      }
    } catch {
      // toast from service
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f4f4f4',
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 440, width: '100%' }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Change password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {introText}
        </Typography>

        <Formik
          initialValues={{
            currentPassword: '',
            password: '',
            confirmedPassword: '',
          }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur }) => (
            <Form>
              <TextField
                fullWidth
                margin="normal"
                type={showCurrentPassword ? 'text' : 'password'}
                name="currentPassword"
                label={currentPasswordLabel}
                value={values.currentPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.currentPassword && Boolean(errors.currentPassword)}
                helperText={touched.currentPassword && errors.currentPassword}
                InputProps={{
                  endAdornment: passwordVisibilityAdornment(
                    showCurrentPassword,
                    () => setShowCurrentPassword((v) => !v)
                  ),
                }}
              />
              <TextField
                fullWidth
                margin="normal"
                type={showPassword ? 'text' : 'password'}
                name="password"
                label="New password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                InputProps={{
                  endAdornment: passwordVisibilityAdornment(
                    showPassword,
                    () => setShowPassword((v) => !v)
                  ),
                }}
              />
              <PasswordRequirementsList password={values.password} showWhenEmpty />
              <TextField
                fullWidth
                margin="normal"
                type={showConfirmedPassword ? 'text' : 'password'}
                name="confirmedPassword"
                label="Confirm new password"
                value={values.confirmedPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.confirmedPassword && Boolean(errors.confirmedPassword)}
                helperText={touched.confirmedPassword && errors.confirmedPassword}
                InputProps={{
                  endAdornment: passwordVisibilityAdornment(
                    showConfirmedPassword,
                    () => setShowConfirmedPassword((v) => !v)
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={submitting}
                sx={{ mt: 3 }}
              >
                {submitting ? 'Saving…' : 'Save and continue'}
              </Button>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default FirstLoginChangePassword;
